import {CommonModule} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {FilterDefinition, FilterValues, VehicleTransaction} from '../../models/vte';
import {OperationsFiltersService} from '../../services/vte/operations-filters.service';
import {VehicleTransactionViewService} from '../../services/vte/vehicle-transaction-view.service';
import {anchorTimeHelp, createdTimeHelp} from '../../services/vte/vision-customer-labels';
import {AuthService} from '../../services/auth.service';
import {VteApiService} from '../../services/vte/vte-api.service';
import {ReportingDateTimeService} from '../../shared/datetime/reporting-datetime.service';
import {
    applyPaginatedResponse,
    createVteListState,
    sortDirectionForField,
    toggleOrdering,
    VteListState,
} from '../../services/vte/vte-list.utils';
import {
    AnalyticsFactRendererComponent,
    FilterBarComponent,
    OperationalAttentionBadgeComponent,
    TransactionStatusBadgeComponent,
    VtePaginationComponent,
} from '../../shared/vte';

@Component({
    selector: 'app-vehicle-transaction-list',
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        FilterBarComponent,
        TransactionStatusBadgeComponent,
        OperationalAttentionBadgeComponent,
        AnalyticsFactRendererComponent,
        VtePaginationComponent,
    ],
    templateUrl: './vehicle-transaction-list.component.html',
    styleUrl: './vehicle-transaction-list.component.css',
})
export class VehicleTransactionListComponent implements OnInit {
    readonly listState: VteListState = createVteListState();
    filterDefinitions: FilterDefinition[] = [];
    filterValues: FilterValues = {};
    transactions: VehicleTransaction[] = [];
    private organizationNames = new Map<number, string>();

    constructor(
        private vteApi: VteApiService,
        private filtersService: OperationsFiltersService,
        private transactionView: VehicleTransactionViewService,
        private route: ActivatedRoute,
        private router: Router,
        private auth: AuthService,
        private reportingDateTime: ReportingDateTimeService,
    ) {
    }

    get showOrganizationColumn(): boolean {
        return this.auth.isSuperUser();
    }

    get listQueryParams(): Record<string, string> {
        const next: Record<string, string> = {};
        this.route.snapshot.queryParamMap.keys.forEach((key) => {
            const value = this.route.snapshot.queryParamMap.get(key);
            if (value) {
                next[key] = value;
            }
        });
        return next;
    }

    ngOnInit(): void {
        this.applyRouteFilters(this.route.snapshot.queryParamMap.get('status_group'));
        this.loadFilterSupportData();
        this.loadTransactions();

        this.route.queryParamMap.subscribe((params) => {
            const statusGroup = params.get('status_group');
            const applied = this.filtersService.getFilterString(this.filterValues, 'status_group') || null;
            if ((statusGroup || null) === applied) {
                return;
            }
            this.applyRouteFilters(statusGroup);
            this.listState.currentPage = 1;
            this.loadFilterSupportData();
            this.loadTransactions();
        });
    }

    private applyRouteFilters(statusGroup: string | null): void {
        const nextValues: FilterValues = {...this.filterValues};
        if (statusGroup) {
            nextValues['status_group'] = statusGroup;
        } else {
            delete nextValues['status_group'];
        }
        this.filterValues = nextValues;
    }

    get showImplementationDetails(): boolean {
        return this.auth.isSuperUser();
    }

    loadFilterSupportData(): void {
        const orgFilter = this.filtersService.getFilterString(this.filterValues, 'org_id');
        this.filtersService.loadAllFilterSupportData(orgFilter).subscribe({
            next: ({cameras, organizations}) => {
                this.organizationNames = new Map(
                    (organizations || []).map((organization) => [organization.id, organization.name]),
                );
                this.filterDefinitions = this.filtersService.getTransactionFilterDefinitions(
                    cameras,
                    organizations,
                );
            },
        });
    }

    organizationLabel(organizationId: number | null | undefined): string {
        if (organizationId == null) {
            return '—';
        }
        return this.organizationNames.get(organizationId) || String(organizationId);
    }

    loadTransactions(): void {
        this.listState.loading = true;
        const params = this.transactionView.buildListParams(
            this.filterValues,
            this.listState.currentPage,
            this.listState.pageSize,
            this.listState.ordering,
        );

        this.vteApi.listVehicleTransactions(params).subscribe({
            next: (response) => {
                this.transactions = response.results || [];
                applyPaginatedResponse(this.listState, response);
                this.listState.loading = false;
            },
            error: () => {
                this.transactions = [];
                this.listState.totalRecords = 0;
                this.listState.loading = false;
            },
        });
    }

    onFiltersApply(values: FilterValues): void {
        this.filterValues = values;
        this.listState.currentPage = 1;
        this.syncRouteQueryParams();
        this.loadFilterSupportData();
        this.loadTransactions();
    }

    onFiltersReset(): void {
        this.filterValues = {};
        this.listState.currentPage = 1;
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {status_group: null},
            queryParamsHandling: 'merge',
        });
        this.loadFilterSupportData();
        this.loadTransactions();
    }

    onPageChange(page: number): void {
        this.listState.currentPage = page;
        this.loadTransactions();
    }

    onPageSizeChange(pageSize: number): void {
        this.listState.pageSize = pageSize;
        this.listState.currentPage = 1;
        this.loadTransactions();
    }

    onSort(field: string): void {
        this.listState.ordering = toggleOrdering(field, this.listState.ordering);
        this.loadTransactions();
    }

    sortIndicator(field: string): string {
        const direction = sortDirectionForField(field, this.listState.ordering);
        if (direction === 'asc') {
            return '↑';
        }
        if (direction === 'desc') {
            return '↓';
        }
        return '';
    }

    previewFacts(transaction: VehicleTransaction) {
        return this.transactionView.buildAnalyticsFacts(transaction).slice(0, 2);
    }

    formatTimestamp(value: string | null | undefined): string {
        if (!value) {
            return '—';
        }
        const formatted = this.reportingDateTime.format(value);
        return formatted || value;
    }

    serialNumber(rowIndex: number): number {
        return (this.listState.currentPage - 1) * this.listState.pageSize + rowIndex + 1;
    }

    columnHelp(column: { helpKey?: string }): string | undefined {
        if (column.helpKey === 'anchor') {
            return anchorTimeHelp(this.showImplementationDetails);
        }
        if (column.helpKey === 'created') {
            return createdTimeHelp();
        }
        return undefined;
    }

    private syncRouteQueryParams(): void {
        const statusGroup = this.filtersService.getFilterString(this.filterValues, 'status_group');
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                status_group: statusGroup || null,
            },
            queryParamsHandling: 'merge',
        });
    }
}
